import { Dispatch } from 'react';
import styled from 'styled-components';

import AlertInformationCircle from '@/components/atom/icon/AlertInformationCircle';
import { BottomSheetTabsListItemProps } from '@/components/molecules/BottomSheet/BottomSheetTabs/BottomSheetTabsList';
import Dropdown from '@/components/molecules/Dropdown';
import theme from '@/styles/theme';
import { Nullable } from '@/types/common';
import { QnaCategoryTag } from '@/types/qna';

import QnaWriteAdditionalInfo, { QnaWriteAdditionalInfoProps } from './QnaWriteAdditionalInfo';

export interface QnaWriteCategoryProps extends QnaWriteAdditionalInfoProps {
  selectedTags: Array<string>;
  onChangeSelectedTags: Dispatch<Array<string>>;
  showCategoriesBottomSheet: boolean;
  selectedCategoriesBottomSheetTabItem: Nullable<BottomSheetTabsListItemProps>;
  toggleCategoriesBottomSheet: (value: boolean) => void;
  isAvailableAiAnswer: boolean;
}

const QnaWriteCategory = ({
  selectedTags,
  onChangeSelectedTags,
  selectedCategoriesBottomSheetTabItem,
  showCategoriesBottomSheet,
  toggleCategoriesBottomSheet,
  onClickCheckPregnant,
  pregnant,
  isAvailableAiAnswer,
}: QnaWriteCategoryProps) => {
  const onSelectTagItem = (tag: QnaCategoryTag) => {
    const tagIndex = selectedTags.findIndex((name) => name === tag.name);
    const list = [...selectedTags];
    if (tagIndex < 0) {
      list.push(tag.name);
    } else {
      list.splice(tagIndex, 1);
    }
    onChangeSelectedTags(list);
  };

  const isSelectedTag = (tag: QnaCategoryTag) => {
    return selectedTags.includes(tag.name);
  };

  return (
    <QuickAnswerCategoryWrapper>
      <Dropdown
        id='quickAnswerCategory'
        label='카테고리'
        labelSpacing='12px'
        placeholder={selectedCategoriesBottomSheetTabItem?.title || '증상•과목 선택'}
        placeHolderColor={theme.color.gray600}
        isOpened={showCategoriesBottomSheet}
        onClickTrigger={() => toggleCategoriesBottomSheet(true)}
      />
      <AdditionalInfoWrapper>
        <QnaWriteAdditionalInfo onClickCheckPregnant={onClickCheckPregnant} pregnant={pregnant} />
      </AdditionalInfoWrapper>
      <QuickAnswerTagsWrapper>
        {selectedCategoriesBottomSheetTabItem?.tags.map((tag) => (
          <Tag key={tag.name} selected={isSelectedTag(tag)} onClick={() => onSelectTagItem(tag)}>
            {tag.name}
          </Tag>
        ))}
      </QuickAnswerTagsWrapper>
      {selectedCategoriesBottomSheetTabItem && (
        <InformationText marginTop={24}>
          <AlertInformationCircle />
          증상에 해당되는 태그를 모두 선택하시면 더욱 정확한 상담을 받을 수 있어요.
        </InformationText>
      )}
      {isAvailableAiAnswer && selectedCategoriesBottomSheetTabItem?.aiAnswerAvailable && (
        <InformationText marginTop={8}>
          <AlertInformationCircle />
          해당 카테고리는 바로 AI 답변을 받을 수 있어요.
        </InformationText>
      )}
    </QuickAnswerCategoryWrapper>
  );
};

export default QnaWriteCategory;

const QuickAnswerCategoryWrapper = styled.div``;

const AdditionalInfoWrapper = styled.div`
  margin-top: 12px;
`;

const QuickAnswerTagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 24px;
  gap: 8px;
`;

const Tag = styled.span<{ selected: boolean }>`
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;

  ${({ theme, selected }) => `
    ${theme.typography.UI14SB};
    color: ${selected ? theme.color.white : theme.color.gray600};
    background: ${selected ? theme.color.gray600 : theme.color.white};
    border: 1px solid ${selected ? theme.color.gray600 : theme.color.gray200};
  `};
`;

const InformationText = styled.div<{ marginTop?: number }>`
  display: flex;

  svg {
    margin: 2px 6px 0 0;
    min-width: 16px;
  }

  ${({ theme, marginTop = 12 }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.blue500};
    margin-top: ${marginTop}px;
  `};
`;
